import React, { useEffect, useState, useRef } from 'react'

import { ScrollView, View, StyleSheet, Keyboard, TouchableOpacity, Text, Platform } from 'react-native'
import delay from 'delay'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Description,
  Space,
  TextInputExtended,
  Select,
  SelectExtended,
  Button,
  Anchor,
  Label,
  KeyboardAvoidingView,
  PaymentHistoryItem,
  BasePlan,
  ProductListItem,
  AnimatedEllipsis
} from '@sart/components'
import { adaptive, backgroundColors, colors, typography, radius } from '@sart/styles'
import client, { orderHelper, useData } from '@sart/client'
import { useContent } from '@sart/content'

import ApiClient from "../bff-client/src/ApiClient";
import CustomersApi from "../bff-client/src/api/CustomersApi";
import SubscriptionsApi from "../bff-client/src/api/SubscriptionsApi";

import AppConfig from '../Configuration/AppConfig';
import { set } from 'lodash'

export default function ChangePlan(props) {
  const { navigation } = props

  const {
    recurringPlans,
    customerId,
    provisionedRecurringAddOns,
    autoRenewEnabled,
    customer,
    promoCodes
  } = client.cache

  const [{ settings, loading, error, pendingCheckLoading, pendingChangePlan, pendingChangePlanMessage, cancellingPendingChange }, setData] = useData({
    loading: false,
    pendingCheckLoading: true,
    pendingChangePlan: false,
    pendingChangePlanMessage: null,
    cancellingPendingChange: false
  })

  const [autoRenew, setAutoRenew] = useState(autoRenewEnabled || true)
  const [discounts, setDiscounts] = useState(null)


  const content = useContent()

  const bffClient = new ApiClient(AppConfig.bffBasePath)
  bffClient.defaultHeaders = {
    ...bffClient.defaultHeaders,
    Origin: AppConfig.shopOriginUrl,
    Authorization: client.api.defaultHeaders['Authorization']
  }
  const customersApi = new CustomersApi(bffClient)
  const subscriptionsApi = new SubscriptionsApi(bffClient)

  console.log('autoRenewEnabled is: ' + autoRenewEnabled)
  console.log('Change Plan discounts are: ' + JSON.stringify(discounts, null, 2))

  useEffect(() => {
    //get the auto renew status on first login/load
    getAutoRenewStatus()
    checkPendingChangePlan()
  }, [])


  async function getAutoRenewStatus() {
    try {
      const email = customer.user.userName
      const autoRenewResp = await customersApi.customersSubscriptionGet(email)
      if (Object.keys(autoRenewResp).length === 0) {
        //empty response so auto renew is on as default
        setAutoRenew(true)
      } else if (autoRenewResp.autoRenew !== undefined) {
        console.log('auto renew status is: ' + JSON.stringify(autoRenewResp))
        setAutoRenew(autoRenewResp.autoRenew)
      } else {
        setAutoRenew(false)
      }

    } catch (error) {
      console.log('error getting auto renew status: ', error)
    }

    //Always update the cache with the auto renew value
    client.setCache({
      autoRenewEnabled: autoRenewEnabled
    })
  }

  async function getSubscriptionDiscounts() {
    try {
      const email = customer.user.userName
      const subscriptionDiscountsResp = await subscriptionsApi.subscriptionsPromoCodesGet(email)
      console.log('subscriptionDiscountsResp is: ' + JSON.stringify(subscriptionDiscountsResp, null, 2))

      if (subscriptionDiscountsResp?.promoCodes?.length > 0) {
        setDiscounts(subscriptionDiscountsResp.promoCodes)
      } else {
        setDiscounts(null)
      }
    } catch (error) {
      console.log('error getting subscription discounts: ', error)
    }
  }



  async function handleSubmit(values, form) {
    Keyboard.dismiss()

    form.setSubmitting(true)
    try {

      form.setSubmitting(false)


    } catch (error) {
      // const message = prettyErrorMessage(error)

      // form.setFieldError('gender', message)
      form.setSubmitting(false)
    }
  }

  function getPendingChangePlanMessage(payload) {
    const pendingPayload = payload?.getActualInstance ? payload.getActualInstance() : payload
    if (!payload) {
      return 'You already have a pending plan change request.'
    }
    if (typeof pendingPayload === 'string') {
      return pendingPayload
    }

    if (pendingPayload?.message) {
      return pendingPayload.message
    }

    if (pendingPayload?.hasPendingChangePlan && pendingPayload?.targetBundle) {
      return `You have a pending change plan to ${pendingPayload.targetBundle}`
    }

    return pendingPayload?.description || pendingPayload?.error || 'You already have a pending plan change request.'
  }

  async function checkPendingChangePlan() {
    const emailAddress = customer?.user?.userName || ''
    if (!emailAddress) {
      setData({ pendingCheckLoading: false, pendingChangePlan: false, pendingChangePlanMessage: null })
      return
    }

    setData({ pendingCheckLoading: true, pendingChangePlan: false, pendingChangePlanMessage: null })
    try {
      const response = await bffClient.callApi(
        '/customers/subscriptions/change-plan', 'GET',
        {}, { email: emailAddress }, {}, {}, null,
        [], [], ['application/json'], Object, null
      )
      const statusCode = response?.response?.status
      const pendingData = response?.data
      const hasPendingMessage = typeof pendingData?.message === 'string' && pendingData.message.trim().length > 0
      const shouldShowPendingChange = statusCode === 200 && hasPendingMessage
      setData({
        pendingCheckLoading: false,
        pendingChangePlan: shouldShowPendingChange,
        pendingChangePlanMessage: shouldShowPendingChange ? getPendingChangePlanMessage(pendingData) : null
      })
    } catch (pendingError) {
      // Any non-200 should keep the existing change-plan screen flow.
      setData({
        pendingCheckLoading: false,
        pendingChangePlan: false,
        pendingChangePlanMessage: null
      })
    }
  }

  async function cancelPendingChangePlan() {
    const emailAddress = customer?.user?.userName || ''
    if (!emailAddress) {
      setData({ error: 'Unable to cancel pending plan change. Missing account email.' })
      return
    }

    setData({ cancellingPendingChange: true, error: null })
    try {
      const cancelProducts = await client.callApi('catalog', 'getModifyServiceDetailsProducts', [client.cache.catalogId])
      const cancelChangePlanProduct = cancelProducts.find(product => product.productLegacyId === AppConfig.cancelChangePlanProduct)

      const extendedData = {
        emailAddress
      }

      const order = await client.checkoutServiceProduct(cancelChangePlanProduct, extendedData, 'noPayment')

      if (order && order.status === 'successful') {
        await checkPendingChangePlan()
        //also set the refresh data for the dashboard so the discounts and products get updated their correctly
        client.setCache({ refreshDashboardData: Date.now() })
      } else {
        setData({ error: 'Cancel change plan failed, please try again later.' })
      }
    } catch (cancelError) {
      console.log('error cancelling pending change plan: ' + JSON.stringify(cancelError, null, 2))
      setData({ error: 'Cancel change plan failed, please try again later.' })
    } finally {
      setData({ cancellingPendingChange: false })
    }
  }

  async function toggleAutoRenew(plan, value) {
    if (pendingChangePlan) {
      return
    }
    //{"emailAddress":"", "autoRenew":"", "bundleName":""}
    console.log('toggling auto renew to: ' + value)
    // console.log('for plan: ' + JSON.stringify(plan,null,2))
    setData({ loading: true })
    setAutoRenew(value)
    const emailAddress = customer?.user?.userName || ''

    try {
      const autoRenewProducts = await client.callApi('catalog', 'getModifyServiceDetailsProducts', [client.cache.catalogId])
      const autoRenewProduct = autoRenewProducts.find(product => product.productLegacyId === AppConfig.autoRenewProduct)

      const extendedData = {
        bundleName: plan?.product?.productLegacyId || '',
        autoRenew: value,
        emailAddress
      }

      const order = await client.checkoutServiceProduct(autoRenewProduct, extendedData)
      if (order.status === "failed") {
        // console.log('order failed so throwing an error')
        //order failed, throw an exception to be caught
        throw new Error(content.menu.orderFailed)
      }

      //if the order was successful, update the BFF and the cache
      await customersApi.updateSubscription({ email: emailAddress, autoRenew: value })
      client.setCache({
        autoRenewEnabled: value
      })



      await delay(2000)

    } catch (error) {
      console.log('error toggling auto renew: ' + JSON.stringify(error, null, 2))
      setAutoRenew(!value)
    }

    setData({ loading: false })
  }

  return (
    <KeyboardAvoidingView style={styles.container}>
      <ScrollView contentContainerStyle={styles.contentContainer}
      // ref={scrollViewRef}
      >
        <View style={styles.widthRestrictions}>
          {pendingCheckLoading &&
            <>
              <Description style={styles.descriptionStyle}></Description>
              <Space height={16} />
            </>
          }

          {!pendingCheckLoading && pendingChangePlan &&
            <>
              {(provisionedRecurringAddOns && provisionedRecurringAddOns.length > 0) &&
                <>
                  <Description style={styles.subTitle}>You Have: </Description>
                  <Space height={8} />
                  {provisionedRecurringAddOns.map((item) =>
                    <BasePlan
                      product={item}
                      productContainer={styles.planContainer}
                      labelStyle={styles.descriptionStyle}
                      nameStyle={styles.descriptionContentStyle}
                      textStyle={styles.dateStyle}
                      productName={item.product.productName}
                      price={item.product.upfrontCost}
                      injectedContent={content}
                      code={item.product.currency}
                      showToggle={false}
                      toggleValue={autoRenew}
                      toggleDisabled={loading}
                      onToggle={async (value) => {
                        await toggleAutoRenew(item, value)
                      }}
                      discounts={promoCodes}
                    />
                  )}
                  <Space height={16} />
                </>
              }
              <View style={styles.pendingChangeContainer}>
                <Description style={styles.pendingChangeMessage}>{pendingChangePlanMessage}</Description>
                <Space height={24} />
                <Button
                  title={cancellingPendingChange ? <AnimatedEllipsis style={{ fontSize: 24, color: colors.clear, ...typography.extrabold }} />
                    : 'Cancel Pending Change'}
                  disabled={cancellingPendingChange}
                  onPress={cancelPendingChangePlan}
                  style={styles.enabledButton}
                  disabledStyle={styles.disabledButton}
                  titleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white }}
                  disabledTitleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white + 45 }}
                />
              </View>
              {error &&
                <>
                  <Space height={16} />
                  <Description style={styles.descriptionStyle}>{error}</Description>
                </>
              }
            </>
          }

          {!pendingCheckLoading && !pendingChangePlan &&
            <>

              {(provisionedRecurringAddOns && provisionedRecurringAddOns.length > 0) &&
                <>
                  <Description style={styles.subTitle}>You Have: </Description>
                  <Space height={8} />

                  {provisionedRecurringAddOns.map((item) =>
                    <BasePlan
                      product={item}
                      productContainer={styles.planContainer}
                      labelStyle={styles.descriptionStyle}
                      nameStyle={styles.descriptionContentStyle}
                      textStyle={styles.dateStyle}
                      productName={item.product.productName}
                      price={item.product.upfrontCost}
                      injectedContent={content}
                      code={item.product.currency}
                      showToggle={true}
                      toggleValue={autoRenew}
                      toggleDisabled={loading}
                      onToggle={async (value) => {
                        await toggleAutoRenew(item, value)
                      }}
                      discounts={discounts}
                    />
                  )}
                  <Space height={16} />
                </>
              }


              {!autoRenew &&
                <>
                  <Description style={[styles.descriptionStyle, { fontSize: 16 }]}>
                    Please enable auto renew in order to change your plan on your next renewal date
                  </Description>
                  <Space height={24} />
                </>
              }

              {(autoRenew && recurringPlans && recurringPlans.length > 0) &&
                <>
                  <Description style={styles.subTitle}>Available Plans: </Description>
                  <Space height={16} />
                </>
              }
              {(autoRenew && recurringPlans && recurringPlans.length > 0) && recurringPlans.map((product) =>
                <>
                  <ProductListItem
                    disabled={false}
                    product={product}
                    onPress={() => navigation.navigate('ChangePlanReview', { newPlan: product, currentPlan: provisionedRecurringAddOns[0] })}
                    priceStyle={styles.price}
                    placeholderStyle={styles.productStyle}
                    bodyStyle={styles.body}
                    imageColor={colors.white}
                    injectedContent={content}
                  />
                  <Space height={16} />
                </>
              )
              }
            </>
          }
        </View>

      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 24,
    paddingBottom: 0,
    backgroundColor: backgroundColors.darkCharcoal,
    ...adaptive.wrapperPadding,
    width: '100%',
  },
  contentContainer: {
    ...Platform.select({
      web: {
        alignItems: 'center'
      }
    })
  },
  dateStyle: {
    fontSize: 14,
    lineHeight: 22,
    color: colors.white,
    ...typography.demi
  },
  planContainer: {
    padding: 12,
    backgroundColor: backgroundColors.darkCharcoal,
    borderRadius: radius.medium,
    elevation: 8,
    // ...shadows.shadowByElevation(8),
    borderColor: colors.white,
    borderWidth: 1
  },
  priceStyle: {
    fontSize: 14,
    lineHeight: 48,
    fontWeight: '700',
    color: colors.white
  },
  descriptionStyle: {
    fontSize: 16,
    lineHeight: 16,
    color: colors.white,
    // textAlign: 'center',
    ...typography.demi,
  },
  descriptionnNoPlanStyle: {
    fontSize: 16,
    lineHeight: 16,
    color: colors.srTextInput,
    ...typography.demi,
  },
  descriptionContentStyle: {
    fontSize: 14,
    lineHeight: 24,
    color: colors.white,
    ...typography.demi
  },
  description: {
    textAlign: 'center',
    ...typography.lightbold,
    color: colors.brightOrange
  },
  selectStyle: {
    ...typography.demi
  },
  curpFooter: {
    ...typography.demi,
    color: colors.brightOrange,
    alignContent: 'flex-end',
    textDecorationLine: 'underline'
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  nestedInputs: {
    flex: 1
  },
  inputStyle: {
    height: 300
  },
  enabledButton: {
    backgroundColor: colors.brightOrange,
  },
  disabledButton: {
    backgroundColor: colors.brightOrange + 50
  },
  label: {
    color: colors.brightOrange,
    ...typography.lightbold,
    fontSize: 16
  },
  helpIcon: {
    justifyContent: 'center'
  },
  helpPointContainer: {
  },
  helpPointText: {
    ...typography.medium,
    color: colors.brightOrange,
    fontSize: 14
  },
  noId: {
    ...typography.demi,
  },
  labelStyles: {
    color: colors.brightOrange,
    ...typography.demi
  },
  enabled: {
    backgroundColor: colors.brightOrange
  },
  disabled: {
    backgroundColor: colors.brightOrange + 60,
    borderColor: colors.brightOrange + 60,
    borderWidth: 1
  },
  subTitle: {
    ...typography.lightbold,
    color: colors.white
  },
  pendingChangeContainer: {
    borderWidth: 1,
    borderColor: colors.white,
    borderRadius: radius.medium,
    padding: 16,
    alignItems: 'center'
  },
  pendingChangeMessage: {
    ...typography.demi,
    color: colors.white,
    textAlign: 'center'
  },
  widthRestrictions: {
    width: '100%',
    maxWidth: 600,
    paddingBottom: 20
  }
})

const selectStyle = {
  control: (base) => ({
    ...base,
    ...typography.demi,
  }),
  menu: (base) => ({
    ...base,
    ...typography.demi
  })
};
